import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  FileCheck, FileX, Clock, Eye, Download, CheckCircle2, XCircle, 
  AlertCircle, Loader2, User, Building2, FileText 
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DocumentReviewPage = () => {
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [docsRes, statsRes] = await Promise.all([
        axios.get(`${API}/documents/pending-review`),
        axios.get(`${API}/documents/review-stats`)
      ]);
      setDocuments(docsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async (doc) => {
    try {
      const response = await axios.get(`${API}/contractors/${doc.contractor_id}/documents/${doc.id}`);
      setPreviewData(response.data);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load document');
    }
  };

  const handleReview = (doc, action) => {
    setSelectedDoc({ ...doc, action });
    setReviewNotes('');
    setReviewDialogOpen(true);
  };

  const submitReview = async () => {
    if (!selectedDoc) return;
    
    setReviewLoading(true);
    try {
      await axios.put(
        `${API}/contractors/${selectedDoc.contractor_id}/documents/${selectedDoc.id}/review`,
        { status: selectedDoc.action, notes: reviewNotes }
      );
      toast.success(`Document ${selectedDoc.action}`);
      setReviewDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update document');
    } finally {
      setReviewLoading(false);
    }
  };

  const getDocTypeLabel = (type) => {
    const labels = {
      qbcc_licence: 'QBCC Licence',
      public_liability: 'Public Liability Insurance',
      workcover: 'WorkCover Certificate',
      professional_indemnity: 'Professional Indemnity'
    };
    return labels[type] || type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-500/20 text-green-500 border-green-500/50"><CheckCircle2 className="h-3 w-3 mr-1" /> Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500/20 text-red-500 border-red-500/50"><XCircle className="h-3 w-3 mr-1" /> Rejected</Badge>;
      default:
        return <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/50"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="document-review-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Document Review</h1>
        <p className="text-muted-foreground mt-1">Review and approve contractor documents</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-yellow-500/10 border-yellow-500/30">
          <CardContent className="p-4 flex items-center gap-4">
            <Clock className="h-8 w-8 text-yellow-500" />
            <div>
              <p className="text-2xl font-bold">{stats.pending}</p>
              <p className="text-sm text-muted-foreground">Pending Review</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="p-4 flex items-center gap-4">
            <CheckCircle2 className="h-8 w-8 text-green-500" />
            <div>
              <p className="text-2xl font-bold">{stats.approved}</p>
              <p className="text-sm text-muted-foreground">Approved</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="p-4 flex items-center gap-4">
            <XCircle className="h-8 w-8 text-red-500" />
            <div>
              <p className="text-2xl font-bold">{stats.rejected}</p>
              <p className="text-sm text-muted-foreground">Rejected</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-primary/10 border-primary/30">
          <CardContent className="p-4 flex items-center gap-4">
            <FileText className="h-8 w-8 text-primary" />
            <div>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-sm text-muted-foreground">Total Documents</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Documents Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-500" />
            Documents Pending Review ({documents.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {documents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500" />
              <p>All documents have been reviewed!</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Contractor</TableHead>
                  <TableHead>Document Type</TableHead>
                  <TableHead>Filename</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{doc.contractor?.name || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground">{doc.contractor?.company_name || doc.contractor?.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{getDocTypeLabel(doc.document_type)}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {doc.filename || 'N/A'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell>{getStatusBadge(doc.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handlePreview(doc)}
                          title="Preview"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-green-500 hover:text-green-600 hover:bg-green-500/10"
                          onClick={() => handleReview(doc, 'approved')}
                          title="Approve"
                        >
                          <FileCheck className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                          onClick={() => handleReview(doc, 'rejected')}
                          title="Reject"
                        >
                          <FileX className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedDoc?.action === 'approved' ? (
                <><FileCheck className="h-5 w-5 text-green-500" /> Approve Document</>
              ) : (
                <><FileX className="h-5 w-5 text-red-500" /> Reject Document</>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-secondary/30 p-4 rounded-lg">
              <p className="text-sm"><strong>Contractor:</strong> {selectedDoc?.contractor?.name}</p>
              <p className="text-sm"><strong>Document:</strong> {getDocTypeLabel(selectedDoc?.document_type)}</p>
              <p className="text-sm"><strong>File:</strong> {selectedDoc?.filename}</p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Review Notes {selectedDoc?.action === 'rejected' && <span className="text-red-500">*</span>}
              </label>
              <Textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder={selectedDoc?.action === 'rejected' 
                  ? "Please explain why this document is being rejected..." 
                  : "Optional notes about the approval..."}
                rows={3}
                className="bg-input border-border"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={submitReview} 
              disabled={reviewLoading || (selectedDoc?.action === 'rejected' && !reviewNotes)}
              className={selectedDoc?.action === 'approved' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              {reviewLoading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</>
              ) : (
                <>{selectedDoc?.action === 'approved' ? 'Approve' : 'Reject'} Document</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" /> Document Preview
            </DialogTitle>
          </DialogHeader>
          
          {previewData && (
            <div className="space-y-4">
              <div className="bg-secondary/30 p-4 rounded-lg grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Document Type</p>
                  <p className="font-medium">{getDocTypeLabel(previewData.document_type)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Filename</p>
                  <p className="font-medium">{previewData.filename}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Uploaded</p>
                  <p className="font-medium">{new Date(previewData.uploaded_at).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  {getStatusBadge(previewData.status)}
                </div>
              </div>
              
              {previewData.file_data && (
                <div className="border rounded-lg overflow-hidden bg-black/50 flex items-center justify-center min-h-[400px]">
                  {previewData.filename?.toLowerCase().endsWith('.pdf') ? (
                    <div className="text-center p-8">
                      <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
                      <p className="text-muted-foreground mb-4">PDF Preview not available</p>
                      <Button 
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = `data:application/pdf;base64,${previewData.file_data}`;
                          link.download = previewData.filename;
                          link.click();
                        }}
                      >
                        <Download className="h-4 w-4 mr-2" /> Download PDF
                      </Button>
                    </div>
                  ) : (
                    <img 
                      src={`data:image/png;base64,${previewData.file_data}`} 
                      alt={previewData.filename}
                      className="max-w-full max-h-[500px] object-contain"
                    />
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>Close</Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => {
                setPreviewDialogOpen(false);
                handleReview(previewData, 'approved');
              }}
            >
              <FileCheck className="h-4 w-4 mr-2" /> Approve
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                setPreviewDialogOpen(false);
                handleReview(previewData, 'rejected');
              }}
            >
              <FileX className="h-4 w-4 mr-2" /> Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentReviewPage;
